import { Controller } from "@hotwired/stimulus"
import { chimeAudible, playChime, setTimerChime, timerChimeEnabled, unlockChime } from "../utils/zen_chime"

// Zen alarm: pick a clock time (or "in 15m") in the Timer modal and Zen rings a bell at it,
// over whatever scene is playing, until you stop or snooze it. The alarm
// lives in localStorage so a reload keeps it; it rings in this tab only, so
// Zen has to stay open (a background tab may ring up to a minute late, which
// is how browsers throttle hidden timers).
//
// While it is set and no timer is running, a ring like a timer's counts down to
// it on the scene, with Test bell / + 5 min / Clear under it. The same modal
// holds the "chime when a timer ends" switch that zen-timer reads
// (utils/zen_chime timerChimeEnabled).
const STORAGE_KEY = "zen-alarm"
const TICK_MS = 1000
const RING_EVERY_MS = 2600
const RING_FOR_MS = 10 * 60 * 1000
// An alarm that came due while Zen was closed still rings if it is this
// fresh; anything older is dropped rather than going off out of nowhere.
const LATE_GRACE_MS = 10 * 60 * 1000
const SNOOZE_MS = 5 * 60 * 1000

export default class extends Controller {
    static targets = ["time", "setRow", "armedRow", "armedLabel", "badge", "badgeLabel", "overlay", "overlayTime", "overlayHint", "chimeToggle",
        "countdown", "countdownRing", "countdownRemaining", "countdownTime"]

    connect() {
        this.alarmAt = null
        this.setAt = null
        this.ringingSince = null
        this.restore()
        if (this.hasChimeToggleTarget) this.chimeToggleTarget.checked = timerChimeEnabled()
        this.sync()
    }

    disconnect() {
        this.stopTick()
        this.stopRinging()
    }

    set(event) {
        if (event) event.preventDefault()
        unlockChime()
        const value = this.hasTimeTarget ? this.timeTarget.value : ""
        const at = this.nextOccurrence(value)
        if (!at) {
            if (this.hasTimeTarget) this.timeTarget.focus()
            return
        }
        this.arm(at)
    }

    // "In 15m / 30m / 1h": a quick alarm from now, no clock maths.
    setIn(event) {
        if (event) event.preventDefault()
        unlockChime()
        const minutes = parseInt(event.currentTarget.dataset.minutes, 10)
        if (!minutes) return
        this.arm(Date.now() + minutes * 60 * 1000)
    }

    addFive(event) {
        if (event) event.preventDefault()
        if (!this.alarmAt) return
        this.arm(this.alarmAt + SNOOZE_MS, { keepStart: true })
    }

    // Hear the bell now, so the alarm holds no surprise (and the browser has the
    // gesture it needs before it lets the bell sound later).
    test(event) {
        if (event) event.preventDefault()
        unlockChime()
        playChime()
    }

    clear(event) {
        if (event) event.preventDefault()
        this.disarm()
    }

    stop(event) {
        if (event) event.preventDefault()
        this.stopRinging()
        this.disarm()
    }

    snooze(event) {
        if (event) event.preventDefault()
        unlockChime()
        this.stopRinging()
        this.arm(Date.now() + SNOOZE_MS)
    }

    // A tap anywhere on the ringing overlay is the gesture a browser needs
    // before it lets the bell sound; it does not stop the alarm.
    wake() {
        unlockChime()
        if (this.ringingSince) playChime()
        this.syncOverlay()
    }

    toggleChime(event) {
        const enabled = event.currentTarget.checked
        setTimerChime(enabled)
        if (enabled) {
            unlockChime()
            playChime({ volume: 0.6 })
        }
    }

    // --- state -----------------------------------------------------------

    arm(at, { keepStart = false } = {}) {
        this.alarmAt = at
        if (!keepStart || !this.setAt) this.setAt = Date.now()
        this.persist()
        this.startTick()
        this.sync()
    }

    disarm() {
        this.alarmAt = null
        this.setAt = null
        this.stopTick()
        this.persist()
        this.sync()
    }

    restore() {
        let at = null
        let setAt = null
        try {
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
            at = parseInt(stored.at, 10)
            setAt = parseInt(stored.set, 10) || null
        } catch (_error) {
            at = null
        }
        if (!at || Number.isNaN(at)) return

        const late = Date.now() - at
        if (late > LATE_GRACE_MS) {
            this.persist()
            return
        }
        this.alarmAt = at
        this.setAt = setAt
        this.startTick()
    }

    persist() {
        try {
            if (this.alarmAt) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: this.alarmAt, set: this.setAt }))
            } else {
                localStorage.removeItem(STORAGE_KEY)
            }
        } catch (_error) {
            // Storage blocked: the alarm still rings while this page is open.
        }
    }

    // "07:30" -> the next time the clock reads 07:30: today if that is still
    // ahead, otherwise tomorrow.
    nextOccurrence(value) {
        const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim())
        if (!match) return null
        const hours = parseInt(match[1], 10)
        const minutes = parseInt(match[2], 10)
        if (hours > 23 || minutes > 59) return null

        const at = new Date()
        at.setHours(hours, minutes, 0, 0)
        if (at.getTime() <= Date.now()) at.setDate(at.getDate() + 1)
        return at.getTime()
    }

    startTick() {
        this.stopTick()
        this.tickTimer = setInterval(() => this.onTick(), TICK_MS)
        this.onTick()
    }

    stopTick() {
        if (this.tickTimer) clearInterval(this.tickTimer)
        this.tickTimer = null
    }

    onTick() {
        if (!this.alarmAt || this.ringingSince) return
        if (Date.now() >= this.alarmAt) {
            this.ring()
            return
        }
        this.syncCountdown()
    }

    // --- ringing ---------------------------------------------------------

    ring() {
        this.stopTick()
        this.ringingSince = Date.now()
        this.originalTitle = document.title
        document.title = `Alarm - ${this.formatClock(this.alarmAt)}`
        playChime()
        this.ringTimer = setInterval(() => {
            if (Date.now() - this.ringingSince > RING_FOR_MS) {
                this.stop()
                return
            }
            playChime()
            this.syncOverlay()
        }, RING_EVERY_MS)
        this.sync()
    }

    stopRinging() {
        if (this.ringTimer) clearInterval(this.ringTimer)
        this.ringTimer = null
        if (this.ringingSince && this.originalTitle) document.title = this.originalTitle
        this.ringingSince = null
    }

    // --- ui --------------------------------------------------------------

    sync() {
        const armed = !!this.alarmAt
        const label = armed ? this.formatClock(this.alarmAt) : ""

        if (this.hasSetRowTarget) this.setRowTarget.hidden = armed
        if (this.hasArmedRowTarget) this.armedRowTarget.hidden = !armed
        if (this.hasArmedLabelTarget) this.armedLabelTarget.textContent = armed ? `Rings at ${label}` : ""
        if (this.hasBadgeTarget) {
            this.badgeTarget.hidden = !armed || !!this.ringingSince
            this.badgeTarget.title = armed ? `Alarm at ${label}` : ""
        }
        if (this.hasBadgeLabelTarget) this.badgeLabelTarget.textContent = label
        this.syncOverlay()
        this.syncCountdown()
    }

    // The ring on the scene: time left to the bell, emptying from when it was
    // set. It gives way to a running timer's own overlay, and to the ringing.
    syncCountdown() {
        if (!this.hasCountdownTarget) return
        const timerShowing = !!this.element.querySelector(".zen-session-overlay:not([hidden]), .zen-breath-overlay:not([hidden]), .zen-wim-overlay:not([hidden])")
        const show = !!this.alarmAt && !this.ringingSince && !timerShowing
        this.countdownTarget.hidden = !show
        if (!show) return

        const left = Math.max(this.alarmAt - Date.now(), 0)
        if (this.hasCountdownRemainingTarget) this.countdownRemainingTarget.textContent = this.formatLeft(left)
        if (this.hasCountdownTimeTarget) this.countdownTimeTarget.textContent = `Alarm ${this.formatClock(this.alarmAt)}`
        if (this.hasCountdownRingTarget) {
            const circumference = 2 * Math.PI * 54
            const total = Math.max(this.alarmAt - (this.setAt || this.alarmAt - left), 1)
            this.countdownRingTarget.style.strokeDashoffset = String(circumference * (1 - left / total))
        }
        if (this.hasArmedLabelTarget) this.armedLabelTarget.textContent = `Rings at ${this.formatClock(this.alarmAt)} - in ${this.formatLeft(left)}`
    }

    // 42:10, or 1:05:22 past the hour.
    formatLeft(ms) {
        const total = Math.ceil(ms / 1000)
        const hours = Math.floor(total / 3600)
        const minutes = Math.floor((total % 3600) / 60)
        const seconds = total % 60
        const pad = (n) => String(n).padStart(2, "0")
        return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
    }

    syncOverlay() {
        if (!this.hasOverlayTarget) return
        const ringing = !!this.ringingSince
        this.overlayTarget.hidden = !ringing
        if (!ringing) return

        if (this.hasOverlayTimeTarget) this.overlayTimeTarget.textContent = this.formatClock(this.alarmAt)
        if (this.hasOverlayHintTarget) this.overlayHintTarget.hidden = chimeAudible()
    }

    formatClock(ms) {
        if (!ms) return ""
        return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    }
}
